import { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Banner, BlockStack, Button, Card, Checkbox, DataTable, Divider, InlineStack, Select, Tabs, Text, TextField } from "@shopify/polaris";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import axios from "axios";
import agent from "../../app/api/agent";
import { formatLocalDateTime } from "./utils/dateTime";
import LegacyDataPanel from "./LegacyDataPanel";
import StoreReviewPanel from "./StoreReviewPanel";
import PilotOperatorTools from "./PilotOperatorTools";
import StoreAuditHistory from "./StoreAuditHistory";
import AdminStoreClassification from "./AdminStoreClassification";
import { AdminAtdAttributeMetafieldMapping, AdminAtdConnectionTestResult, AdminAtdCreateTestProductResponse, AdminAtdFilteredProductSample, AdminAtdProfile, AdminFitmentImportHistoryItem, AdminShopRuntime, AdminStoreDetails, AdminStorefrontImageFallbackEvent, AdminStoreGeneralSettings, AdminStoreShopifyAppCredentials, AdminWebhookAudit } from "../../app/models/Admin";

type AdminConsoleContext = {
  token: string;
  onUnauthorized: () => void;
};

const defaultAtdProfile: AdminAtdProfile = {
  name: "Primary ATD Profile",
  standardBaseUrl: "https://testws.atdconnect.com",
  standardApiVersion: "3.6",
  fitmentBaseUrl: "https://testws.atdconnect.com",
  fitmentApiVersion: "3.6",
  shipToHomeBaseUrl: "https://sthuat.atdconnect.com",
  shipToHomeApiVersion: "1.1",
  clientId: "",
  username: "",
  password: "",
  secretKey: "",
  accountNumber: "",
  locationNumber: "",
  useShipToHomeCatalog: false,
  isActive: false,
  enableAtdPull: false,
  enableAtdPushToShopify: false,
  enableFitmentPull: false,
  enableFitmentPushToShopify: false,
  enablePricePull: false,
  enablePricePushToShopify: false,
  createProductsAsDraft: false,
  syncExistingProductDetailsFromAtd: false,
  publishProductsToOnlineStore: false,
  useBrandProductNameSizeTitleFormat: false,
  testModeEnabled: false,
  testModeCreateLimit: 25,
  sizeAttributeValues: [],
  attributeMetafieldMappings: [],
};

const defaultShopifyAppCredentials: AdminStoreShopifyAppCredentials = {
  appName: "",
  clientId: "",
  secretKey: "",
  appHandle: "",
  clearSecretKey: false,
  hasSecretKey: false,
  isConfigured: false,
};

const createDefaultGeneralSettings = (): AdminStoreGeneralSettings => ({
  searchEnablePersistentFiltering: false,
  submodelEnabled: false,
  useYmmPrefixForProductTags: true,
  inventoryAutoMapEnabled: false,
  inventoryLocationIds: [],
  inventoryLocations: [],
});

const atdMetafieldTypeOptions = [
  { label: "Single line text", value: "single_line_text_field" },
  { label: "Integer", value: "number_integer" },
  { label: "Decimal", value: "number_decimal" },
  { label: "Dimension", value: "dimension" },
  { label: "Weight", value: "weight" },
  { label: "True/False", value: "boolean" },
];

const syncSettingMetadata: Record<string, { label: string; description: string }> = {
  "Shopify.AccessTokenAuthorizationHold": {
    label: "Access token authorization hold",
    description: "Temporarily pauses Shopify API actions when token authorization needs attention.",
  },
  "Shopify.BulkSyncIntervalHours": {
    label: "Bulk sync interval (hours)",
    description: "Full product/variant reconciliation cadence. Default is 168 hours (weekly).",
  },
  "Shopify.FullSyncStaggerWindowHours": {
    label: "Full sync stagger window (hours)",
    description: "Spreads full product/variant pulls across this window using a stable shop-id slot.",
  },
  "Shopify.IncrementalSyncIntervalMinutes": {
    label: "Product/variant incremental sync interval (minutes)",
    description: "Number of minutes between Shopify product/variant updated_at refreshes.",
  },
  "Shopify.CollectionIncrementalSyncIntervalMinutes": {
    label: "Collection incremental sync interval (minutes)",
    description: "Number of minutes between Shopify collection updated_at refreshes.",
  },
  "Shopify.EnableCollectionMembershipSync": {
    label: "Enable collection membership sync",
    description: "Allows Shopify collection membership batches to be queued for this store.",
  },
  "Shopify.ProductMetafieldSyncIntervalHours": {
    label: "Product metafield sync interval (hours)",
    description: "Number of hours between product metafield refreshes, including review metafields.",
  },
  "Shopify.UpdatedAtOverlapMinutes": {
    label: "Updated_at overlap (minutes)",
    description: "Safety overlap applied to incremental Shopify updated_at queries.",
  },
  "Shopify.EnableInventorySync": {
    label: "Enable inventory sync",
    description: "Enables syncing inventory quantities from PartFits to Shopify.",
  },
  "Shopify.EnableInventoryWebhooks": {
    label: "Enable inventory webhooks",
    description: "Registers inventory_levels/update webhooks when inventory sync is enabled.",
  },
  "Shopify.EnableLocationWebhooks": {
    label: "Enable location webhooks",
    description: "Registers location create/update/delete webhooks when inventory sync is enabled.",
  },
  "Shopify.EnableFulfillmentInventoryMapPush": {
    label: "Enable fulfillment inventory-map push",
    description: "Allows inventory reconciliation/webhook changes to push fulfillment inventory-map metafields.",
  },
  "Shopify.EnableProductUpdatesWebhook": {
    label: "Enable product updates webhook handling",
    description: "Processes product update webhook events received from Shopify.",
  },
  "Shopify.EnableWebhooks": {
    label: "Enable webhook subscriptions",
    description: "Allows webhook subscription management and webhook-driven sync flows.",
  },
  "Shopify.InventoryAutoMapSyncIntervalHours": {
    label: "Inventory auto-map sync interval (hours)",
    description: "Number of hours between automatic inventory mapping sync attempts.",
  },
  "Shopify.InventoryBulkSyncIntervalHours": {
    label: "Inventory bulk sync interval (hours)",
    description: "Number of hours between full inventory bulk synchronization runs.",
  },
  "Shopify.LocationSyncIntervalHours": {
    label: "Location sync interval (hours)",
    description: "Number of hours between scheduled Shopify location synchronization runs.",
  },
  "Shopify.RequestFullProductSync": {
    label: "Request full product sync",
    description: "Flag used to queue a full product resync on the next sync cycle.",
  },
  "Shopify.WebhookSurgeModeActive": {
    label: "Webhook surge mode active",
    description: "Indicates whether webhook surge protection mode is currently active.",
  },
  "Turn14.EnableMetafieldSync": {
    label: "Enable Turn14 metafield sync",
    description: "Enables syncing Turn14-related data into Shopify metafields.",
  },
  "Turn14.EnableMpnTitleSync": {
    label: "Enable Turn14 MPN title sync",
    description: "Enables updating product titles using Turn14 MPN-based title rules.",
  },
  "Atd.ProductTitle.UseBrandProductNameSizeFormat": {
    label: "ATD title format: Brand - Product Name - Size",
    description: "Formats ATD-created and ATD-updated Shopify product titles from brand, product name, and size.",
  },
};

type CuratedSyncSetting = {
  key: string;
  type: "boolean" | "number";
  defaultValue: string;
};

const curatedSyncSettings: CuratedSyncSetting[] = [
  { key: "Shopify.EnableWebhooks", type: "boolean", defaultValue: "True" },
  { key: "Shopify.EnableProductUpdatesWebhook", type: "boolean", defaultValue: "False" },
  { key: "Shopify.EnableInventorySync", type: "boolean", defaultValue: "False" },
  { key: "Shopify.EnableInventoryWebhooks", type: "boolean", defaultValue: "True" },
  { key: "Shopify.EnableLocationWebhooks", type: "boolean", defaultValue: "True" },
  { key: "Shopify.EnableFulfillmentInventoryMapPush", type: "boolean", defaultValue: "True" },
  { key: "Shopify.BulkSyncIntervalHours", type: "number", defaultValue: "168" },
  { key: "Shopify.FullSyncStaggerWindowHours", type: "number", defaultValue: "168" },
  { key: "Shopify.IncrementalSyncIntervalMinutes", type: "number", defaultValue: "240" },
  { key: "Shopify.CollectionIncrementalSyncIntervalMinutes", type: "number", defaultValue: "120" },
  { key: "Shopify.EnableCollectionMembershipSync", type: "boolean", defaultValue: "True" },
  { key: "Shopify.ProductMetafieldSyncIntervalHours", type: "number", defaultValue: "24" },
  { key: "Shopify.UpdatedAtOverlapMinutes", type: "number", defaultValue: "30" },
  { key: "Shopify.InventoryBulkSyncIntervalHours", type: "number", defaultValue: "24" },
  { key: "Shopify.InventoryAutoMapSyncIntervalHours", type: "number", defaultValue: "24" },
  { key: "Shopify.LocationSyncIntervalHours", type: "number", defaultValue: "24" },
  { key: "Shopify.RequestFullProductSync", type: "boolean", defaultValue: "False" },
  { key: "Shopify.WebhookSurgeModeActive", type: "boolean", defaultValue: "False" },
];

const formatSyncSettingLabel = (key: string): string => {
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getSyncSettingDescription = (key: string): string => {
  const curated = syncSettingMetadata[key]?.description;
  if (curated) return curated;
  return "Store-specific sync value used by backend synchronization logic.";
};

const extractApiErrorMessage = (error: unknown): string | null => {
  if (!axios.isAxiosError(error)) return null;

  const responseData = error.response?.data;
  if (typeof responseData === "string" && responseData.trim().length > 0) {
    return responseData.trim();
  }

  if (
    responseData
    && typeof responseData === "object"
    && "message" in responseData
    && typeof responseData.message === "string"
    && responseData.message.trim().length > 0
  ) {
    return responseData.message.trim();
  }

  return null;
};

const shortenTelemetryValue = (value?: string, maxLength = 78) => {
  const trimmed = value?.trim();
  if (!trimmed) return "-";
  if (trimmed.length <= maxLength) return trimmed;

  const prefixLength = Math.max(16, maxLength - 24);
  return `${trimmed.slice(0, prefixLength)}...${trimmed.slice(-18)}`;
};

const renderTelemetryLink = (value?: string, maxLength = 78) => {
  const trimmed = value?.trim();
  if (!trimmed) return "-";

  if (/^https?:\/\//i.test(trimmed)) {
    return (
      <a href={trimmed} target="_blank" rel="noreferrer" title={trimmed} style={{ wordBreak: "break-all" }}>
        {shortenTelemetryValue(trimmed, maxLength)}
      </a>
    );
  }

  return <span title={trimmed} style={{ wordBreak: "break-all" }}>{shortenTelemetryValue(trimmed, maxLength)}</span>;
};

const getStorefrontProductUrl = (event: AdminStorefrontImageFallbackEvent, storeDomain?: string) => {
  const productUrl = event.productUrl?.trim();
  if (!productUrl) return undefined;
  if (/^https?:\/\//i.test(productUrl)) return productUrl;
  if (productUrl.startsWith("/") && storeDomain) return `https://${storeDomain}${productUrl}`;
  return undefined;
};

const renderImageFallbackProduct = (event: AdminStorefrontImageFallbackEvent, storeDomain?: string) => {
  const label = event.productHandle || event.productUrl || "-";
  const productUrl = getStorefrontProductUrl(event, storeDomain);

  if (!productUrl) {
    return <span title={event.productUrl || event.productHandle}>{shortenTelemetryValue(label, 48)}</span>;
  }

  return (
    <a href={productUrl} target="_blank" rel="noreferrer" title={productUrl}>
      {shortenTelemetryValue(label, 48)}
    </a>
  );
};


export default function AdminStoreDetailsPage() {
  const { token, onUnauthorized } = useOutletContext<AdminConsoleContext>();
  const navigate = useNavigate();
  const { shopId } = useParams();
  const [details, setDetails] = useState<AdminStoreDetails | null>(null);
  const [runtime, setRuntime] = useState<AdminShopRuntime | null>(null);
  const [webhookAudit, setWebhookAudit] = useState<AdminWebhookAudit | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingRuntime, setLoadingRuntime] = useState(false);
  const [repairingWebhooks, setRepairingWebhooks] = useState(false);
  const [useExtendedAccessTokenForRuntime, setUseExtendedAccessTokenForRuntime] = useState(true);
  const [savingActiveState, setSavingActiveState] = useState(false);
  const [deletingWebhookId, setDeletingWebhookId] = useState<string | null>(null);
  const [deletingAllWebhooks, setDeletingAllWebhooks] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [lastLoadedAt, setLastLoadedAt] = useState<Date | null>(null);
  const [editedSettings, setEditedSettings] = useState<Record<string, string>>({});
  const [savingSettingKey, setSavingSettingKey] = useState<string | null>(null);
  const [generalSettings, setGeneralSettings] = useState<AdminStoreGeneralSettings>(createDefaultGeneralSettings);
  const [savingGeneralSettings, setSavingGeneralSettings] = useState(false);
  const [shopifyAppCredentials, setShopifyAppCredentials] = useState<AdminStoreShopifyAppCredentials>(defaultShopifyAppCredentials);
  const [savingShopifyAppCredentials, setSavingShopifyAppCredentials] = useState(false);
  const [atdProfile, setAtdProfile] = useState<AdminAtdProfile>(defaultAtdProfile);
  const [savingAtdProfile, setSavingAtdProfile] = useState(false);
  const [testingAtdProfile, setTestingAtdProfile] = useState(false);
  const [atdTestResult, setAtdTestResult] = useState<AdminAtdConnectionTestResult | null>(null);
  const [testingAtdFilterProducts, setTestingAtdFilterProducts] = useState(false);
  const [atdFilterProductSamples, setAtdFilterProductSamples] = useState<AdminAtdFilteredProductSample[]>([]);
  const [atdTestPartNumber, setAtdTestPartNumber] = useState("");
  const [creatingAtdTestProduct, setCreatingAtdTestProduct] = useState(false);
  const [atdTestProductResult, setAtdTestProductResult] = useState<AdminAtdCreateTestProductResponse | null>(null);
  const [clearingSurgeMode, setClearingSurgeMode] = useState(false);
  const [downloadingImportQueueId, setDownloadingImportQueueId] = useState<number | null>(null);
  const [deletingImportQueueId, setDeletingImportQueueId] = useState<number | null>(null);
  const [restartingImportQueueId, setRestartingImportQueueId] = useState<number | null>(null);

  const parsedShopId = Number(shopId);

  const sortedSettings = useMemo(() => {
    if (!details) return [];
    return Object.entries(details.store.syncSettings).sort(([left], [right]) => left.localeCompare(right));
  }, [details]);

  const loadDetails = useCallback(async () => {
    if (!Number.isFinite(parsedShopId)) {
      setError("Invalid store id.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await agent.Admin.storeDetails(token, parsedShopId);
      setDetails(response);
      setEditedSettings(response.store.syncSettings || {});
      setGeneralSettings(response.generalSettings || createDefaultGeneralSettings());
      setShopifyAppCredentials({
        ...defaultShopifyAppCredentials,
        ...(response.shopifyAppCredentials || {}),
        secretKey: "",
        clearSecretKey: false,
      });
      setAtdProfile(response.atdProfile || defaultAtdProfile);
      setAtdTestResult(null);
      setAtdFilterProductSamples([]);
      setAtdTestProductResult(null);
      setAtdTestPartNumber("");
      setLastLoadedAt(new Date());
    } catch {
      setError("Unable to load store details.");
      onUnauthorized();
    } finally {
      setLoading(false);
    }
  }, [parsedShopId, token, onUnauthorized]);

  const loadRuntime = useCallback(async (silent = false, useExtendedAccessToken = useExtendedAccessTokenForRuntime) => {
    if (!Number.isFinite(parsedShopId)) return;

    if (!silent) setLoadingRuntime(true);

    try {
      const response = await agent.Admin.shopRuntime(token, parsedShopId, useExtendedAccessToken);
      setRuntime(response);
      if (!silent) setLastLoadedAt(new Date());
    } catch {
      if (!silent) setError("Unable to load Shopify runtime status.");
    } finally {
      if (!silent) setLoadingRuntime(false);
    }
  }, [parsedShopId, token, useExtendedAccessTokenForRuntime]);

  const loadWebhookAudit = useCallback(async () => {
    if (!Number.isFinite(parsedShopId)) return;

    try {
      const response = await agent.Admin.webhookAudit(token, parsedShopId);
      setWebhookAudit(response);
    } catch {
      setError("Unable to load webhook audit.");
    }
  }, [parsedShopId, token]);

  useEffect(() => {
    loadDetails();
    loadRuntime();
    loadWebhookAudit();
  }, [loadDetails, loadRuntime, loadWebhookAudit]);

  useEffect(() => {
    if (!Number.isFinite(parsedShopId)) return;
    const id = window.setInterval(() => {
      loadRuntime(true);
    }, 10000);

    return () => window.clearInterval(id);
  }, [parsedShopId, loadRuntime]);

  const handleIsActiveChange = async (newValue: boolean) => {
    if (!details) return;

    setSavingActiveState(true);
    setError(null);

    try {
      const updated = await agent.Admin.updateStoreActiveStatus(token, details.store.shopId, newValue);
      setDetails((current) => current ? { ...current, store: { ...current.store, isActive: updated.isActive, updatedAt: updated.updatedAt } } : current);
    } catch {
      setError("Unable to update active status.");
    } finally {
      setSavingActiveState(false);
    }
  };

  const handleSettingEdit = (key: string, value: string) => {
    setEditedSettings((current) => ({ ...current, [key]: value }));
  };

  const handleSettingSave = async (key: string) => {
    if (!details) return;

    const value = editedSettings[key] ?? "";
    setSavingSettingKey(key);
    setError(null);

    try {
      const updated = await agent.Admin.updateStoreSyncSetting(token, details.store.shopId, key, value);
      setDetails((current) => current ? { ...current, store: { ...current.store, syncSettings: updated.syncSettings, updatedAt: updated.updatedAt } } : current);
      setEditedSettings(updated.syncSettings || {});
      if (key.includes("Webhook") || key === "Shopify.EnableWebhooks" || key === "Shopify.EnableInventorySync") {
        void loadWebhookAudit();
      }
    } catch {
      setError(`Unable to update setting: ${key}.`);
    } finally {
      setSavingSettingKey(null);
    }
  };


  const handleGeneralSettingChange = (
    key: "searchEnablePersistentFiltering" | "submodelEnabled" | "useYmmPrefixForProductTags" | "inventoryAutoMapEnabled",
    value: boolean
  ) => {
    setGeneralSettings((current) => ({ ...current, [key]: value }));
  };

  const handleInventoryLocationToggle = (locationId: number, selected: boolean) => {
    setGeneralSettings((current) => {
      const currentIds = current.inventoryLocationIds || [];
      const nextIds = selected
        ? Array.from(new Set([...currentIds, locationId])).sort((left, right) => left - right)
        : currentIds.filter((id) => id !== locationId);

      return {
        ...current,
        inventoryLocationIds: nextIds,
        inventoryLocations: (current.inventoryLocations || []).map((location) =>
          location.id === locationId ? { ...location, selected } : location
        ),
      };
    });
  };

  const handleGeneralSettingsSave = async () => {
    if (!details) return;

    setSavingGeneralSettings(true);
    setError(null);

    try {
      const updated = await agent.Admin.updateStoreGeneralSettings(token, details.store.shopId, generalSettings);
      setGeneralSettings(updated);
      setDetails((current) => current ? { ...current, generalSettings: updated } : current);
    } catch {
      setError("Unable to update general store settings.");
    } finally {
      setSavingGeneralSettings(false);
    }
  };

  const handleShopifyAppCredentialChange = <K extends keyof AdminStoreShopifyAppCredentials>(key: K, value: AdminStoreShopifyAppCredentials[K]) => {
    setShopifyAppCredentials((current) => ({ ...current, [key]: value }));
  };

  const handleShopifyAppCredentialsSave = async () => {
    if (!details) return;

    setSavingShopifyAppCredentials(true);
    setError(null);

    try {
      const updated = await agent.Admin.updateStoreShopifyAppCredentials(token, details.store.shopId, shopifyAppCredentials);
      setShopifyAppCredentials({
        ...defaultShopifyAppCredentials,
        ...updated,
        secretKey: "",
        clearSecretKey: false,
      });
      setDetails((current) => current ? { ...current, shopifyAppCredentials: updated } : current);
    } catch {
      setError("Unable to update Shopify app credentials.");
    } finally {
      setSavingShopifyAppCredentials(false);
    }
  };

  const handleClearSurgeMode = async () => {
    if (!details) return;

    const confirmed = window.confirm("Clear webhook surge mode and reset surge counters for this store?");
    if (!confirmed) return;

    setClearingSurgeMode(true);
    setError(null);

    try {
      const updated = await agent.Admin.clearStoreSurgeMode(token, details.store.shopId);
      setDetails((current) => current ? { ...current, store: { ...current.store, syncSettings: updated.syncSettings, updatedAt: updated.updatedAt } } : current);
      setEditedSettings(updated.syncSettings || {});
    } catch {
      setError("Unable to clear surge mode.");
    } finally {
      setClearingSurgeMode(false);
    }
  };

  const handleAtdProfileChange = <K extends keyof AdminAtdProfile>(key: K, value: AdminAtdProfile[K]) => {
    setAtdProfile((current) => ({ ...current, [key]: value }));
  };

  const handleAddAttributeMapping = () => {
    setAtdProfile((current) => ({
      ...current,
      attributeMetafieldMappings: [...(current.attributeMetafieldMappings || []), { atdAttributeName: "", shopifyProductMetafield: "", shopifyMetafieldType: "single_line_text_field" }],
    }));
  };

  const handleUpdateAttributeMapping = (index: number, field: keyof AdminAtdAttributeMetafieldMapping, value: string) => {
    setAtdProfile((current) => {
      const next = [...(current.attributeMetafieldMappings || [])];
      if (!next[index]) return current;
      next[index] = { ...next[index], [field]: value };
      return { ...current, attributeMetafieldMappings: next };
    });
  };

  const handleRemoveAttributeMapping = (index: number) => {
    setAtdProfile((current) => ({
      ...current,
      attributeMetafieldMappings: (current.attributeMetafieldMappings || []).filter((_, i) => i !== index),
    }));
  };

  const handleSaveAtdProfile = async () => {
    if (!details) return;

    // Save and test are intentionally separate so credential edits can be staged without
    // overwriting the last saved values unless the user explicitly persists them.
    setSavingAtdProfile(true);
    setError(null);

    try {
      const updated = await agent.Admin.updateStoreAtdProfile(token, details.store.shopId, atdProfile);
      setAtdProfile(updated);
      setDetails((current) => current ? { ...current, atdProfile: updated } : current);
    } catch {
      setError("Unable to save ATD profile.");
    } finally {
      setSavingAtdProfile(false);
    }
  };

  const handleTestAtdProfile = async () => {
    if (!details) return;

    // Test against the current form state, not just the last saved state, so credentials can be
    // validated before saving them for the store.
    setTestingAtdProfile(true);
    setError(null);

    try {
      const result = await agent.Admin.testStoreAtdProfile(token, details.store.shopId, atdProfile);
      setAtdTestResult(result);
    } catch {
      setError("Unable to test ATD connection.");
    } finally {
      setTestingAtdProfile(false);
    }
  };

  const handleTestAtdFilterProducts = async () => {
    if (!details) return;

    setTestingAtdFilterProducts(true);
    setError(null);

    try {
      const result = await agent.Admin.testStoreAtdProfileProducts(token, details.store.shopId, atdProfile);
      setAtdFilterProductSamples(result);
    } catch {
      setError("Unable to test ATD size filters.");
    } finally {
      setTestingAtdFilterProducts(false);
    }
  };

  const handleCreateAtdTestProduct = async () => {
    if (!details) return;

    // Keep validation client-side too so operators get immediate feedback
    // before triggering the server-side ATD/Shopify sync call.
    const trimmedPartNumber = atdTestPartNumber.trim();
    if (!trimmedPartNumber) {
      setError("Enter an ATD part number before creating a test product.");
      return;
    }

    setCreatingAtdTestProduct(true);
    setError(null);

    try {
      const result = await agent.Admin.createStoreAtdTestProduct(
        token,
        details.store.shopId,
        trimmedPartNumber
      );
      setAtdTestProductResult(result);
    } catch (error) {
      const apiErrorMessage = extractApiErrorMessage(error);
      setError(apiErrorMessage ?? "Unable to create ATD test product.");
    } finally {
      setCreatingAtdTestProduct(false);
    }
  };


  const handleDeleteWebhook = async (id: string) => {
    if (!Number.isFinite(parsedShopId)) return;

    const confirmed = window.confirm(`Delete webhook subscription ${id}?`);
    if (!confirmed) return;

    setDeletingWebhookId(id);
    setError(null);

    try {
      await agent.Admin.deleteWebhookSubscription(token, parsedShopId, id);
      await loadRuntime();
      await loadWebhookAudit();
    } catch {
      setError("Unable to delete webhook subscription.");
    } finally {
      setDeletingWebhookId(null);
    }
  };

  const handleDeleteAllWebhooks = async () => {
    if (!Number.isFinite(parsedShopId) || !runtime || runtime.webhookSubscriptions.length === 0) return;

    const confirmed = window.confirm(`Delete all ${runtime.webhookSubscriptions.length} webhook subscriptions?`);
    if (!confirmed) return;

    setDeletingAllWebhooks(true);
    setError(null);

    try {
      for (const subscription of runtime.webhookSubscriptions) {
        await agent.Admin.deleteWebhookSubscription(token, parsedShopId, subscription.id);
      }

      await loadRuntime();
      await loadWebhookAudit();
    } catch {
      setError("Unable to delete all webhook subscriptions.");
    } finally {
      setDeletingAllWebhooks(false);
    }
  };



  const handleDownloadFitmentImport = async (item: AdminFitmentImportHistoryItem) => {
    if (!details) return;

    setDownloadingImportQueueId(item.queueId);
    setError(null);

    try {
      const response = await agent.Admin.downloadStoreFitmentImportFile(token, details.store.shopId, item.queueId);
      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = item.originalFileName || `fitment-import-${item.queueId}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      setError("Unable to download fitment import file.");
    } finally {
      setDownloadingImportQueueId(null);
    }
  };

  const handleDeleteFitmentImport = async (item: AdminFitmentImportHistoryItem) => {
    if (!details) return;

    const confirmed = window.confirm(`Delete fitment import history item ${item.queueId}?`);
    if (!confirmed) return;

    setDeletingImportQueueId(item.queueId);
    setError(null);

    try {
      await agent.Admin.deleteStoreFitmentImportHistoryItem(token, details.store.shopId, item.queueId);
      setDetails((current) => current
        ? {
            ...current,
            fitmentImportHistory: current.fitmentImportHistory.filter((historyItem) => historyItem.queueId !== item.queueId),
          }
        : current);
    } catch {
      setError("Unable to delete fitment import history item.");
    } finally {
      setDeletingImportQueueId(null);
    }
  };

  const handleRestartFitmentImport = async (item: AdminFitmentImportHistoryItem) => {
    if (!token || !shopId) {
      return;
    }

    const confirmed = window.confirm(`Restart fitment import ${item.queueId} using the existing uploaded file?`);
    if (!confirmed) {
      return;
    }

    setRestartingImportQueueId(item.queueId);
    setError(null);

    try {
      await agent.Admin.restartStoreFitmentImportHistoryItem(token, Number(shopId), item.queueId);
      await loadDetails();
    } catch (restartError) {
      console.error(restartError);
      setError("Unable to restart fitment import history item.");
    } finally {
      setRestartingImportQueueId(null);
    }
  };


  const handleUseExtendedAccessTokenForRuntimeChange = (checked: boolean) => {
    setUseExtendedAccessTokenForRuntime(checked);
    void loadRuntime(false, checked);
  };

  const handleRepairWebhooks = async () => {
    if (!Number.isFinite(parsedShopId)) return;

    setRepairingWebhooks(true);
    setError(null);

    try {
      const response = await agent.Admin.repairWebhookAudit(token, parsedShopId);
      setWebhookAudit(response);
      await loadRuntime();
    } catch {
      setError("Unable to repair webhook subscriptions.");
    } finally {
      setRepairingWebhooks(false);
    }
  };

  const forceRefresh = async () => {
    await Promise.all([loadDetails(), loadRuntime(), loadWebhookAudit()]);
  };

  const tabs = [
    { id: "overview", content: "Overview", accessibilityLabel: "Overview" },
    { id: "runtime", content: "Shopify runtime", accessibilityLabel: "Shopify runtime" },
    { id: "settings", content: "Sync settings", accessibilityLabel: "Sync settings" },
    { id: "atd", content: "ATD", accessibilityLabel: "ATD" },
    { id: "notes", content: "Notes & flags", accessibilityLabel: "Notes & flags" },
    { id: "fitment-history", content: "Fitment history", accessibilityLabel: "Fitment history" },
    { id: "legacy-data", content: "Legacy data", accessibilityLabel: "Legacy data" },
    { id: "audit-history", content: "Audit history", accessibilityLabel: "Audit history" },
  ];

  const endpointText = (sub: AdminShopRuntime["webhookSubscriptions"][number]) => {
    if (sub.callbackUrl) return sub.callbackUrl;
    if (sub.arn) return sub.arn;
    if (sub.pubSubProject || sub.pubSubTopic) return `${sub.pubSubProject || "-"} / ${sub.pubSubTopic || "-"}`;
    return "-";
  };

  const getCuratedSettingValue = (definition: CuratedSyncSetting) => {
    return editedSettings[definition.key] ?? details?.store.syncSettings[definition.key] ?? definition.defaultValue;
  };

  const parseBooleanSettingValue = (value: string) => {
    const normalized = value.trim().toLowerCase();
    return normalized === "true" || normalized === "1" || normalized === "yes";
  };

  const imageFallbackEvents = details?.imageFallbackEvents || [];
  const openImageFallbackEvents = imageFallbackEvents.filter((event) => !event.resolvedAt);
  const imageFallbackHitCount = imageFallbackEvents.reduce((total, event) => total + (event.hitCount || 0), 0);

  const alerts = useMemo(() => {
    if (!details) return [];

    const unresolvedImageFallbackCount = (details.imageFallbackEvents || []).filter((event) => !event.resolvedAt).length;

    const parseSettingBool = (key: string) => {
      const rawValue = details.store.syncSettings[key];
      if (!rawValue) return false;

      const normalized = rawValue.trim().toLowerCase();
      return normalized === "true" || normalized === "1" || normalized === "yes";
    };

    const nextAlerts: Array<{ title: string; tone: "warning" | "critical" | "info"; body: string }> = [];

    if (parseSettingBool("Shopify.WebhookSurgeModeActive")) {
      nextAlerts.push({
        title: "Webhook surge mode is active",
        tone: "critical",
        body: "Incoming webhook processing may be intentionally skipped while surge mode remains enabled.",
      });
    }

    if (!parseSettingBool("Shopify.EnableWebhooks")) {
      nextAlerts.push({
        title: "Webhook processing is disabled",
        tone: "warning",
        body: "This store is configured with Shopify webhooks disabled, so near real-time product updates will not run.",
      });
    }

    if (parseSettingBool("Shopify.AccessTokenAuthorizationHold")) {
      nextAlerts.push({
        title: "Shop access token is on authorization hold",
        tone: "critical",
        body: "The store token is currently held; Shopify API operations may be blocked until authorization is restored.",
      });
    }

    if (parseSettingBool("Shopify.RequestFullProductSync")) {
      nextAlerts.push({
        title: "Full product sync requested",
        tone: "info",
        body: "A full product sync has been requested and may increase processing load until completed.",
      });
    }

    if (runtime?.currentBulkOperation?.errorCode) {
      nextAlerts.push({
        title: "Current bulk operation has an error",
        tone: "critical",
        body: `Shopify reports error code: ${runtime.currentBulkOperation.errorCode}.`,
      });
    }

    if (unresolvedImageFallbackCount > 0) {
      nextAlerts.push({
        title: "Storefront image fallback telemetry",
        tone: "warning",
        body: `${unresolvedImageFallbackCount} products have unresolved search-result image fallback events.`,
      });
    }

    return nextAlerts;
  }, [details, runtime]);

  return (
    <BlockStack gap="400">
      <InlineStack align="space-between" blockAlign="center">
        <Button variant="plain" onClick={() => navigate("/pf-console/stores")}>← Back to stores</Button>
        <InlineStack gap="200">
          <Button onClick={loadDetails} loading={loading}>Refresh details</Button>
          <Button onClick={() => loadRuntime()} loading={loadingRuntime}>Refresh runtime</Button>
          <Button variant="primary" onClick={forceRefresh} loading={loading || loadingRuntime}>Force refresh (bypass stale view)</Button>
        </InlineStack>
      </InlineStack>

      {error ? <Text as="p" tone="critical">{error}</Text> : null}
      <Text as="p" tone="subdued">Store details are loaded into the page state; use refresh buttons to reload latest DB/runtime values. Last refreshed: {lastLoadedAt ? lastLoadedAt.toLocaleString() : "not yet"}.</Text>

      {loading ? (
        <Card><Text as="p">Loading store details...</Text></Card>
      ) : !details ? (
        <Card><Text as="p">No details found.</Text></Card>
      ) : (
        <>
          <Card>
            <BlockStack gap="200">
              <Text as="h2" variant="headingLg">{details.store.name || details.store.domain || `Shop ${details.store.shopId}`}</Text>
              <AdminStoreClassification classification={details.storeClassification}>
                <Badge tone={details.store.isActive ? "success" : "warning"}>{details.store.isActive ? "Active" : "Inactive"}</Badge>
                <Text as="span" tone="subdued">{details.store.domain || details.store.myShopifyDomain || "-"}</Text>
              </AdminStoreClassification>
              {alerts.map((alert) => (
                <Banner key={alert.title} title={alert.title} tone={alert.tone}>
                  <p>{alert.body}</p>
                </Banner>
              ))}
            </BlockStack>
          </Card>

          <Card>
            <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab} />
            <Divider />
            <div style={{ paddingTop: "0.75rem" }}>
              {selectedTab === 7 ? <StoreAuditHistory key={details.store.shopId} shopId={details.store.shopId} token={token} onUnauthorized={onUnauthorized} /> : null}
              {selectedTab === 6 ? <LegacyDataPanel key={details.store.shopId} shopId={details.store.shopId}
                sourceShopId={details.legacySourceShopId} token={token} onUnauthorized={onUnauthorized} /> : null}
              {selectedTab === 0 ? (
                <BlockStack gap="400">
                  <DataTable
                    columnContentTypes={["text", "text"]}
                    headings={["Field", "Value"]}
                    rows={[
                      ["Domain", details.store.domain || details.store.myShopifyDomain || "-"],
                      ["Subscription", `${details.store.subscription?.name || "-"} (${details.store.subscription?.status || "no status"})`],
                      ["Plan", `${details.store.planDisplayName || "-"} | Dev: ${String(details.store.planPartnerDevelopment ?? false)} | Plus: ${String(details.store.planShopifyPlus ?? false)}`],
                      ["Emails", `${details.store.email || "-"} / ${details.store.contactEmail || "-"}`],
                      ["Product count", `${details.store.productCount}`],
                      ["Last sync", formatLocalDateTime(details.store.lastSyncAt)],
                      ["User last seen", formatLocalDateTime(details.store.userLastAccessedAt)],
                      ["Shop updated", formatLocalDateTime(details.store.updatedAt)],
                    ]}
                  />
                  <Checkbox
                    label="Shop is active"
                    checked={details.store.isActive}
                    onChange={handleIsActiveChange}
                    disabled={savingActiveState}
                  />
                  <Divider />
                  <BlockStack gap="200">
                    <InlineStack align="space-between" blockAlign="center">
                      <Text as="h3" variant="headingMd">Shopify app credentials</Text>
                      <Badge tone={shopifyAppCredentials.isConfigured ? "success" : "info"}>
                        {shopifyAppCredentials.isConfigured ? "Store override enabled" : "Default app"}
                      </Badge>
                    </InlineStack>
                    <Text as="p" tone="subdued">
                      Map this store to a specific Shopify app record. Leave the secret blank to keep the current saved secret.
                    </Text>
                    <InlineStack gap="300" align="start">
                      <div style={{ minWidth: "260px", flex: 1 }}>
                        <TextField
                          label="App name"
                          autoComplete="off"
                          value={shopifyAppCredentials.appName || ""}
                          onChange={(value) => handleShopifyAppCredentialChange("appName", value)}
                          disabled={savingShopifyAppCredentials}
                        />
                      </div>
                      <div style={{ minWidth: "260px", flex: 1 }}>
                        <TextField
                          label="App handle"
                          autoComplete="off"
                          value={shopifyAppCredentials.appHandle || ""}
                          onChange={(value) => handleShopifyAppCredentialChange("appHandle", value)}
                          disabled={savingShopifyAppCredentials}
                        />
                      </div>
                    </InlineStack>
                    <InlineStack gap="300" align="start">
                      <div style={{ minWidth: "320px", flex: 1 }}>
                        <TextField
                          label="Client ID / API key"
                          autoComplete="off"
                          value={shopifyAppCredentials.clientId || ""}
                          onChange={(value) => handleShopifyAppCredentialChange("clientId", value)}
                          disabled={savingShopifyAppCredentials}
                        />
                      </div>
                      <div style={{ minWidth: "320px", flex: 1 }}>
                        <TextField
                          label="API secret key"
                          type="password"
                          autoComplete="off"
                          value={shopifyAppCredentials.secretKey || ""}
                          onChange={(value) => handleShopifyAppCredentialChange("secretKey", value)}
                          disabled={savingShopifyAppCredentials || shopifyAppCredentials.clearSecretKey}
                          helpText={shopifyAppCredentials.hasSecretKey ? "A secret is saved. Leave blank to keep it." : "No secret saved for this store."}
                        />
                      </div>
                    </InlineStack>
                    {shopifyAppCredentials.hasSecretKey ? (
                      <Checkbox
                        label="Clear saved API secret key"
                        checked={shopifyAppCredentials.clearSecretKey || false}
                        onChange={(checked) => handleShopifyAppCredentialChange("clearSecretKey", checked)}
                        disabled={savingShopifyAppCredentials}
                      />
                    ) : null}
                    <InlineStack align="end">
                      <Button
                        variant="primary"
                        onClick={handleShopifyAppCredentialsSave}
                        loading={savingShopifyAppCredentials}
                        disabled={savingShopifyAppCredentials}
                      >
                        Save Shopify app credentials
                      </Button>
                    </InlineStack>
                  </BlockStack>
                </BlockStack>
              ) : null}

              {selectedTab === 1 ? (
                <BlockStack gap="400">
                  <Text as="p" tone="subdued">Auto-refreshes every 10 seconds.</Text>
                  <Card>
                    <BlockStack gap="200">
                      <InlineStack align="space-between" blockAlign="center">
                        <Text as="h3" variant="headingMd">Access scopes</Text>
                        <Badge tone={runtime?.usingExtendedAccessToken ? "success" : "info"}>
                          {runtime?.usingExtendedAccessToken ? "Using extended token" : "Using standard token"}
                        </Badge>
                      </InlineStack>
                      <Checkbox
                        label="Use extended access token (if available)"
                        checked={useExtendedAccessTokenForRuntime}
                        onChange={handleUseExtendedAccessTokenForRuntimeChange}
                        disabled={loadingRuntime}
                      />
                      {!runtime || runtime.accessScopes.length === 0 ? (
                        <Text as="p">No access scopes returned by Shopify.</Text>
                      ) : (
                        <DataTable
                          columnContentTypes={["text"]}
                          headings={["Scope"]}
                          rows={runtime.accessScopes.map((scope) => [scope])}
                        />
                      )}
                    </BlockStack>
                  </Card>
                  <Card>
                    <BlockStack gap="200">
                      <Text as="h3" variant="headingMd">Current bulk operation</Text>
                      {runtime?.currentBulkOperation ? (
                        <DataTable
                          columnContentTypes={["text", "text"]}
                          headings={["Field", "Value"]}
                          rows={[
                            ["ID", runtime.currentBulkOperation.id || "-"],
                            ["Status", runtime.currentBulkOperation.status || "-"],
                            ["Error", runtime.currentBulkOperation.errorCode || "-"],
                            ["Created", formatLocalDateTime(runtime.currentBulkOperation.createdAt)],
                            ["Completed", formatLocalDateTime(runtime.currentBulkOperation.completedAt)],
                            ["Object count", `${runtime.currentBulkOperation.objectCount ?? "-"}`],
                            ["File size", `${runtime.currentBulkOperation.fileSize ?? "-"}`],
                            ["Result URL", runtime.currentBulkOperation.url || "-"],
                            ["Partial data URL", runtime.currentBulkOperation.partialDataUrl || "-"],
                          ]}
                        />
                      ) : (
                        <Text as="p">No bulk operation currently returned by Shopify.</Text>
                      )}
                    </BlockStack>
                  </Card>
                  <Card>
                    <BlockStack gap="200">
                      <InlineStack align="space-between" blockAlign="center">
                        <Text as="h3" variant="headingMd">Webhook subscriptions</Text>
                        <Button
                          tone="critical"
                          disabled={!runtime || runtime.webhookSubscriptions.length === 0 || deletingAllWebhooks || deletingWebhookId !== null}
                          loading={deletingAllWebhooks}
                          onClick={handleDeleteAllWebhooks}
                        >
                          Delete all
                        </Button>
                      </InlineStack>
                      {!runtime || runtime.webhookSubscriptions.length === 0 ? (
                        <Text as="p">No webhook subscriptions found.</Text>
                      ) : (
                        <DataTable
                          columnContentTypes={["text", "text", "text", "text"]}
                          headings={["Topic", "Endpoint type", "Endpoint", "Action"]}
                          rows={runtime.webhookSubscriptions.map((sub) => [
                            sub.topic,
                            sub.endpointType || "-",
                            endpointText(sub),
                            <Button
                              key={`delete-${sub.id}`}
                              variant="plain"
                              tone="critical"
                              disabled={deletingAllWebhooks || deletingWebhookId === sub.id}
                              onClick={() => handleDeleteWebhook(sub.id)}
                            >
                              Delete
                            </Button>
                          ])}
                        />
                      )}
                    </BlockStack>
                  </Card>
                  <Card>
                    <BlockStack gap="200">
                      <Text as="h3" variant="headingMd">Sync health</Text>
                      {(details.syncStates || []).length === 0 ? (
                        <Text as="p">No sync state rows have been recorded yet.</Text>
                      ) : (
                        <DataTable
                          columnContentTypes={["text", "text", "text", "text", "text", "numeric"]}
                          headings={["Object", "Property", "Related", "Status", "Last completed", "Count"]}
                          rows={(details.syncStates || []).slice(0, 20).map((state) => [
                            state.syncObject || "-",
                            state.propertyName || "-",
                            state.relatedEntityId ? String(state.relatedEntityId) : "-",
                            state.lastStatus || "-",
                            formatLocalDateTime(state.lastCompletedAt),
                            `${state.lastObjectCount ?? "-"}`
                          ])}
                        />
                      )}
                    </BlockStack>
                  </Card>
                  <Card>
                    <BlockStack gap="200">
                      <InlineStack align="space-between" blockAlign="center">
                        <InlineStack gap="200" blockAlign="center">
                          <Text as="h3" variant="headingMd">Storefront image fallbacks</Text>
                          <Badge tone={openImageFallbackEvents.length > 0 ? "warning" : "success"}>
                            {openImageFallbackEvents.length > 0 ? `${openImageFallbackEvents.length} open` : "No open events"}
                          </Badge>
                        </InlineStack>
                        <Text as="span" tone="subdued">{imageFallbackHitCount} hits</Text>
                      </InlineStack>
                      {imageFallbackEvents.length === 0 ? (
                        <Text as="p">No image fallback events logged.</Text>
                      ) : (
                        <DataTable
                          columnContentTypes={["text", "text", "numeric", "text", "text", "text"]}
                          headings={["Status", "Product", "Hits", "First seen", "Last seen", "Image URLs"]}
                          rows={imageFallbackEvents.slice(0, 50).map((event) => [
                            <InlineStack key={`status-${event.id}`} gap="100" blockAlign="center">
                              <Badge tone={event.resolvedAt ? "success" : "warning"}>
                                {event.resolvedAt ? "Resolved" : "Open"}
                              </Badge>
                              {event.resolvedAt ? (
                                <Text as="span" tone="subdued">{formatLocalDateTime(event.resolvedAt)}</Text>
                              ) : null}
                            </InlineStack>,
                            renderImageFallbackProduct(event, details.store.domain || details.store.myShopifyDomain),
                            `${event.hitCount ?? 0}`,
                            formatLocalDateTime(event.firstSeenAt),
                            formatLocalDateTime(event.lastSeenAt),
                            <BlockStack key={`urls-${event.id}`} gap="100">
                              <Text as="span">Failed: {renderTelemetryLink(event.failedImageUrl, 90)}</Text>
                              <Text as="span">Fallback: {renderTelemetryLink(event.fallbackImageUrl, 90)}</Text>
                            </BlockStack>
                          ])}
                        />
                      )}
                    </BlockStack>
                  </Card>
                  <Card>
                    <BlockStack gap="200">
                      <Text as="h3" variant="headingMd">Bulk operation queue</Text>
                      {(details.bulkQueue || []).length === 0 ? (
                        <Text as="p">No queued bulk operations.</Text>
                      ) : (
                        <DataTable
                          columnContentTypes={["text", "text", "text", "text", "text", "text"]}
                          headings={["Status", "Object", "Property", "Related", "Requested", "Bulk operation"]}
                          rows={(details.bulkQueue || []).slice(0, 20).map((item) => [
                            item.status,
                            item.syncObject,
                            item.propertyName,
                            item.relatedEntityId ? String(item.relatedEntityId) : "-",
                            formatLocalDateTime(item.requestedAt),
                            item.bulkOperationGid || "-"
                          ])}
                        />
                      )}
                    </BlockStack>
                  </Card>
                  <Card>
                    <BlockStack gap="200">
                      <InlineStack align="space-between" blockAlign="center">
                        <InlineStack gap="200" blockAlign="center">
                          <Text as="h3" variant="headingMd">Webhook audit</Text>
                          {webhookAudit ? (
                            <Badge tone={webhookAudit.hasDrift ? "critical" : "success"}>
                              {webhookAudit.hasDrift ? "Drift found" : "In sync"}
                            </Badge>
                          ) : null}
                        </InlineStack>
                        <InlineStack gap="200">
                          <Button onClick={loadWebhookAudit}>Refresh audit</Button>
                          <Button variant="primary" onClick={handleRepairWebhooks} loading={repairingWebhooks} disabled={repairingWebhooks}>
                            Repair managed webhooks
                          </Button>
                        </InlineStack>
                      </InlineStack>
                      {!webhookAudit ? (
                        <Text as="p">No webhook audit loaded.</Text>
                      ) : webhookAudit.items.length === 0 ? (
                        <Text as="p">No expected or actual webhook subscriptions.</Text>
                      ) : (
                        <DataTable
                          columnContentTypes={["text", "text", "text", "text", "text"]}
                          headings={["Topic", "Status", "Expected", "Actual", "Callback"]}
                          rows={webhookAudit.items.map((item) => [
                            item.topic,
                            item.status,
                            item.expected ? "Yes" : "No",
                            item.actual ? "Yes" : "No",
                            item.callbackUrl || "-"
                          ])}
                        />
                      )}
                    </BlockStack>
                  </Card>
                </BlockStack>
              ) : null}

              {selectedTab === 2 ? (
                <BlockStack gap="300">
                  <Card>
                    <BlockStack gap="200">
                      <Text as="h3" variant="headingMd">MMY behavior settings</Text>
                      <Text as="p" tone="subdued">
                        Settings that control how make/model/year behavior works for storefront filtering and tag generation.
                      </Text>
                      <Checkbox
                        label="Enable persistent filtering on vehicle search"
                        checked={generalSettings.searchEnablePersistentFiltering}
                        onChange={(checked) => handleGeneralSettingChange("searchEnablePersistentFiltering", checked)}
                        disabled={savingGeneralSettings}
                      />
                      <Checkbox
                        label="Enable submodel"
                        checked={generalSettings.submodelEnabled}
                        onChange={(checked) => handleGeneralSettingChange("submodelEnabled", checked)}
                        disabled={savingGeneralSettings}
                      />
                      <Checkbox
                        label="Use ymm_ prefix for product tags"
                        checked={generalSettings.useYmmPrefixForProductTags}
                        onChange={(checked) => handleGeneralSettingChange("useYmmPrefixForProductTags", checked)}
                        disabled={savingGeneralSettings}
                      />
                      <InlineStack align="end">
                        <Button variant="primary" onClick={handleGeneralSettingsSave} loading={savingGeneralSettings} disabled={savingGeneralSettings}>
                          Save MMY behavior settings
                        </Button>
                      </InlineStack>
                    </BlockStack>
                  </Card>

                  <Card>
                    <BlockStack gap="200">
                      <Text as="h3" variant="headingMd">Global inventory auto-map</Text>
                      <Text as="p" tone="subdued">
                        Select Shopify locations that should be automatically mapped to every tracked product when missing. This is the global app setting, not a data feed profile setting.
                      </Text>
                      <Checkbox
                        label="Ensure selected locations are mapped to every product"
                        checked={!!generalSettings.inventoryAutoMapEnabled}
                        onChange={(checked) => handleGeneralSettingChange("inventoryAutoMapEnabled", checked)}
                        disabled={savingGeneralSettings}
                      />
                      {(generalSettings.inventoryLocations || []).length === 0 ? (
                        <Text as="p">No Shopify locations are currently available for this store.</Text>
                      ) : (
                        <DataTable
                          columnContentTypes={["text", "text", "text", "text"]}
                          headings={["Selected", "Location", "Address", "Status"]}
                          rows={(generalSettings.inventoryLocations || []).map((location) => {
                            const selected = (generalSettings.inventoryLocationIds || []).includes(location.id);
                            const statusBits = [
                              location.isPrimary ? "Primary" : "",
                              location.isActive ? "" : "Inactive",
                            ].filter(Boolean);

                            return [
                              <Checkbox
                                key={`inventory-location-selected-${location.id}`}
                                label=""
                                checked={selected}
                                onChange={(checked) => handleInventoryLocationToggle(location.id, checked)}
                                disabled={savingGeneralSettings || !generalSettings.inventoryAutoMapEnabled}
                              />,
                              location.name || `Location ${location.id}`,
                              location.address || "-",
                              statusBits.join(" / ") || "-"
                            ];
                          })}
                        />
                      )}
                      <InlineStack align="end">
                        <Button variant="primary" onClick={handleGeneralSettingsSave} loading={savingGeneralSettings} disabled={savingGeneralSettings}>
                          Save global inventory settings
                        </Button>
                      </InlineStack>
                    </BlockStack>
                  </Card>

                  <Card>
                    <BlockStack gap="200">
                      <InlineStack align="space-between" blockAlign="center">
                        <Text as="h3" variant="headingMd">Core sync controls</Text>
                        <Button
                          tone="critical"
                          onClick={handleClearSurgeMode}
                          loading={clearingSurgeMode}
                          disabled={clearingSurgeMode || savingSettingKey !== null}
                        >
                          Clear surge mode + counts
                        </Button>
                      </InlineStack>
                      <DataTable
                        columnContentTypes={["text", "text", "text", "text"]}
                        headings={["Setting", "Description", "Value", "Action"]}
                        rows={curatedSyncSettings.map((definition) => {
                          const value = getCuratedSettingValue(definition);
                          const isSaving = savingSettingKey === definition.key;
                          const label = syncSettingMetadata[definition.key]?.label ?? formatSyncSettingLabel(definition.key);
                          const description = getSyncSettingDescription(definition.key);

                          return [
                            <code key={`curated-name-${definition.key}`}>{label}</code>,
                            description,
                            <div key={`curated-value-${definition.key}`} style={{ minWidth: definition.type === "number" ? "160px" : "260px" }}>
                              {definition.type === "boolean" ? (
                                <Checkbox
                                  label={parseBooleanSettingValue(value) ? "Enabled" : "Disabled"}
                                  checked={parseBooleanSettingValue(value)}
                                  onChange={(checked) => handleSettingEdit(definition.key, checked ? "True" : "False")}
                                  disabled={isSaving}
                                />
                              ) : (
                                <TextField
                                  label={`Value for ${definition.key}`}
                                  labelHidden
                                  type="number"
                                  autoComplete="off"
                                  value={value}
                                  onChange={(nextValue) => handleSettingEdit(definition.key, nextValue)}
                                  disabled={isSaving}
                                />
                              )}
                            </div>,
                            <Button
                              key={`curated-save-${definition.key}`}
                              size="slim"
                              variant="primary"
                              onClick={() => handleSettingSave(definition.key)}
                              loading={isSaving}
                              disabled={isSaving}
                            >
                              Save
                            </Button>
                          ];
                        })}
                      />
                    </BlockStack>
                  </Card>

                  {sortedSettings.length === 0 ? (
                    <Text as="p">No explicit shop sync settings saved.</Text>
                  ) : (
                    <Card>
                      <BlockStack gap="200">
                        <Text as="p" tone="subdued">Edit values and click save per row to update any explicit store sync setting.</Text>
                        <DataTable
                          columnContentTypes={["text", "text", "text", "text", "text"]}
                          headings={["Label", "Setting name", "Description", "Value", "Action"]}
                          rows={sortedSettings.map(([key]) => {
                            const value = editedSettings[key] ?? "";
                            const isSaving = savingSettingKey === key;
                            const label = syncSettingMetadata[key]?.label ?? formatSyncSettingLabel(key);
                            const description = getSyncSettingDescription(key);

                            return [
                              label,
                              <code key={`name-${key}`}>{key}</code>,
                              description,
                              <div key={`value-${key}`} style={{ minWidth: "360px" }}>
                                <TextField
                                  label={`Value for ${key}`}
                                  labelHidden
                                  autoComplete="off"
                                  value={value}
                                  onChange={(nextValue) => handleSettingEdit(key, nextValue)}
                                  disabled={isSaving}
                                />
                              </div>,
                              <Button
                                key={`save-${key}`}
                                size="slim"
                                variant="primary"
                                onClick={() => handleSettingSave(key)}
                                loading={isSaving}
                                disabled={isSaving}
                              >
                                Save
                              </Button>
                            ];
                          })}
                        />
                      </BlockStack>
                    </Card>
                  )}
                </BlockStack>
              ) : null}


              {selectedTab === 3 ? (
                <BlockStack gap="300">
                  <Card>
                    <BlockStack gap="200">
                      <InlineStack align="space-between" blockAlign="center">
                        <Text as="h3" variant="headingMd">ATD profile</Text>
                        <Badge tone={atdProfile.isActive ? "success" : "attention"}>
                          {atdProfile.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </InlineStack>
                      <Text as="p" tone="subdued">
                        ATD connection settings are stored per store so you can manage credentials and endpoint families (Standard, Fitment, and Ship-to-Home) independently.
                      </Text>
                      {atdTestResult ? (
                        <Banner
                          title={atdTestResult.isSuccess ? "ATD connection test succeeded" : "ATD connection test failed"}
                          tone={atdTestResult.isSuccess ? "success" : "critical"}
                        >
                          <p>{atdTestResult.message || "No message returned."}</p>
                          <p>Tested: {formatLocalDateTime(atdTestResult.testedAt)}</p>
                          <p>Auth endpoint: {atdTestResult.authEndpoint || "-"}</p>
                          <p>Catalog endpoint: {atdTestResult.catalogEndpoint || "-"}</p>
                          <p>Status: {atdTestResult.httpStatusCode ?? "-"}</p>
                          {atdTestResult.responseSnippet ? <p>Response: {atdTestResult.responseSnippet}</p> : null}
                        </Banner>
                      ) : null}
                      <TextField
                        label="Profile name"
                        autoComplete="off"
                        value={atdProfile.name}
                        onChange={(value) => handleAtdProfileChange("name", value)}
                      />
                      {/* ATD exposes different endpoint families. Keep each URL/version editable independently. */}
                      <InlineStack gap="300" align="start">
                        <div style={{ minWidth: "320px", flex: 1 }}>
                          <TextField
                            label="Standard base URL"
                            autoComplete="off"
                            value={atdProfile.standardBaseUrl}
                            onChange={(value) => handleAtdProfileChange("standardBaseUrl", value)}
                          />
                        </div>
                        <div style={{ minWidth: "140px" }}>
                          <TextField
                            label="Standard API version"
                            autoComplete="off"
                            value={atdProfile.standardApiVersion}
                            onChange={(value) => handleAtdProfileChange("standardApiVersion", value)}
                          />
                        </div>
                      </InlineStack>
                      <InlineStack gap="300" align="start">
                        <div style={{ minWidth: "320px", flex: 1 }}>
                          <TextField
                            label="Fitment base URL"
                            autoComplete="off"
                            value={atdProfile.fitmentBaseUrl}
                            onChange={(value) => handleAtdProfileChange("fitmentBaseUrl", value)}
                          />
                        </div>
                        <div style={{ minWidth: "140px" }}>
                          <TextField
                            label="Fitment API version"
                            autoComplete="off"
                            value={atdProfile.fitmentApiVersion}
                            onChange={(value) => handleAtdProfileChange("fitmentApiVersion", value)}
                          />
                        </div>
                      </InlineStack>
                      <InlineStack gap="300" align="start">
                        <div style={{ minWidth: "320px", flex: 1 }}>
                          <TextField
                            label="Ship-to-home base URL"
                            autoComplete="off"
                            value={atdProfile.shipToHomeBaseUrl}
                            onChange={(value) => handleAtdProfileChange("shipToHomeBaseUrl", value)}
                          />
                        </div>
                        <div style={{ minWidth: "180px" }}>
                          <TextField
                            label="STH API version"
                            autoComplete="off"
                            value={atdProfile.shipToHomeApiVersion}
                            onChange={(value) => handleAtdProfileChange("shipToHomeApiVersion", value)}
                          />
                        </div>
                      </InlineStack>
                      <InlineStack gap="300" align="start">
                        <div style={{ minWidth: "260px", flex: 1 }}>
                          <TextField
                            label="Client ID"
                            autoComplete="off"
                            value={atdProfile.clientId}
                            onChange={(value) => handleAtdProfileChange("clientId", value)}
                          />
                        </div>
                        <div style={{ minWidth: "260px", flex: 1 }}>
                          <TextField
                            label="Username"
                            autoComplete="off"
                            value={atdProfile.username}
                            onChange={(value) => handleAtdProfileChange("username", value)}
                          />
                        </div>
                      </InlineStack>
                      <InlineStack gap="300" align="start">
                        <div style={{ minWidth: "260px", flex: 1 }}>
                          <TextField
                            label="Password"
                            autoComplete="off"
                            type="password"
                            value={atdProfile.password}
                            onChange={(value) => handleAtdProfileChange("password", value)}
                          />
                        </div>
                        <div style={{ minWidth: "260px", flex: 1 }}>
                          <TextField
                            label="Secret key"
                            autoComplete="off"
                            type="password"
                            value={atdProfile.secretKey}
                            onChange={(value) => handleAtdProfileChange("secretKey", value)}
                          />
                        </div>
                      </InlineStack>
                      <InlineStack gap="300" align="start">
                        <div style={{ minWidth: "220px", flex: 1 }}>
                          <TextField
                            label="Account number"
                            autoComplete="off"
                            value={atdProfile.accountNumber}
                            onChange={(value) => handleAtdProfileChange("accountNumber", value)}
                          />
                        </div>
                        <div style={{ minWidth: "220px", flex: 1 }}>
                          <TextField
                            label="Location number"
                            autoComplete="off"
                            value={atdProfile.locationNumber}
                            onChange={(value) => handleAtdProfileChange("locationNumber", value)}
                          />
                        </div>
                      </InlineStack>
                      <Checkbox
                        label="Use ship-to-home catalog"
                        checked={atdProfile.useShipToHomeCatalog}
                        onChange={(checked) => handleAtdProfileChange("useShipToHomeCatalog", checked)}
                      />
                      <Checkbox
                        label="ATD profile is active"
                        checked={atdProfile.isActive}
                        onChange={(checked) => handleAtdProfileChange("isActive", checked)}
                      />
                      <Checkbox
                        label="Enable ATD pull"
                        checked={atdProfile.enableAtdPull}
                        onChange={(checked) => handleAtdProfileChange("enableAtdPull", checked)}
                      />
                      <Checkbox
                        label="Enable ATD push to Shopify"
                        checked={atdProfile.enableAtdPushToShopify}
                        onChange={(checked) => handleAtdProfileChange("enableAtdPushToShopify", checked)}
                      />
                      <Checkbox
                        label="Enable fitment pull"
                        checked={atdProfile.enableFitmentPull}
                        onChange={(checked) => handleAtdProfileChange("enableFitmentPull", checked)}
                        disabled={!atdProfile.enableAtdPull}
                      />
                      <Checkbox
                        label="Enable fitment push to Shopify"
                        checked={atdProfile.enableFitmentPushToShopify}
                        onChange={(checked) => handleAtdProfileChange("enableFitmentPushToShopify", checked)}
                        disabled={!atdProfile.enableAtdPushToShopify}
                      />
                      <Checkbox
                        label="Enable price pull"
                        checked={atdProfile.enablePricePull}
                        onChange={(checked) => handleAtdProfileChange("enablePricePull", checked)}
                        disabled={!atdProfile.enableAtdPull}
                      />
                      <Checkbox
                        label="Enable price push to Shopify"
                        checked={atdProfile.enablePricePushToShopify}
                        onChange={(checked) => handleAtdProfileChange("enablePricePushToShopify", checked)}
                        disabled={!atdProfile.enableAtdPushToShopify}
                      />
                      <Checkbox
                        label="Create new Shopify products as draft"
                        checked={atdProfile.createProductsAsDraft}
                        onChange={(checked) => handleAtdProfileChange("createProductsAsDraft", checked)}
                      />
                      <Checkbox
                        label="Overwrite existing Shopify product details from ATD"
                        checked={atdProfile.syncExistingProductDetailsFromAtd}
                        onChange={(checked) => handleAtdProfileChange("syncExistingProductDetailsFromAtd", checked)}
                        helpText="Leave off to preserve merchant edits after initial ATD product creation. Inventory still syncs, and discontinued ATD products are archived."
                      />
                      <Checkbox
                        label="Publish active ATD products to Online Store"
                        checked={atdProfile.publishProductsToOnlineStore}
                        onChange={(checked) => handleAtdProfileChange("publishProductsToOnlineStore", checked)}
                        helpText="Adds active ATD-synced products to the Online Store sales channel. Draft and archived products remain hidden."
                      />
                      <Checkbox
                        label="Format Shopify product titles as Brand - Product Name - Size"
                        checked={atdProfile.useBrandProductNameSizeTitleFormat}
                        onChange={(checked) => handleAtdProfileChange("useBrandProductNameSizeTitleFormat", checked)}
                        helpText="Uses the ATD brand, product name, and Size attribute for new ATD products and for existing products when ATD detail overwrites are enabled."
                      />
                      <Checkbox
                        label="Enable test mode (limit new product creates)"
                        checked={atdProfile.testModeEnabled}
                        onChange={(checked) => handleAtdProfileChange("testModeEnabled", checked)}
                      />
                      <div style={{ maxWidth: "320px" }}>
                        <TextField
                          label="Test mode create limit"
                          type="number"
                          autoComplete="off"
                          value={String(atdProfile.testModeCreateLimit ?? 25)}
                          min={1}
                          disabled={!atdProfile.testModeEnabled}
                          helpText="When test mode is enabled, only this many new products are queued for Shopify create per prep run."
                          onChange={(value) => {
                            const parsed = Number.parseInt(value, 10);
                            handleAtdProfileChange("testModeCreateLimit", Number.isFinite(parsed) && parsed > 0 ? parsed : 0);
                          }}
                        />
                      </div>
                      <TextField
                        label="Size attribute values to include"
                        autoComplete="off"
                        multiline={3}
                        helpText='Only products with attribute name "size" and one of these values will be eligible for sync. Enter comma or newline separated values.'
                        value={atdProfile.sizeAttributeValues.join(", ")}
                        onChange={(value) => handleAtdProfileChange("sizeAttributeValues", value.split(/[\n,;]+/).map((item) => item.trim()).filter(Boolean))}
                      />
                      <Divider />
                      <BlockStack gap="200">
                        <InlineStack align="space-between" blockAlign="center">
                          <Text as="h4" variant="headingSm">ATD attribute to Shopify metafield mappings</Text>
                          <Button onClick={handleAddAttributeMapping}>Add mapping</Button>
                        </InlineStack>
                        <Text as="p" tone="subdued">Map an ATD attribute name to a Shopify product metafield using namespace.key format.</Text>
                        {(atdProfile.attributeMetafieldMappings || []).length === 0 ? (
                          <Text as="p" tone="subdued">No attribute mappings configured.</Text>
                        ) : (
                          <DataTable
                            columnContentTypes={["text", "text", "text", "text"]}
                            headings={["ATD attribute", "Shopify product metafield (namespace.key)", "Shopify type", "Action"]}
                            rows={(atdProfile.attributeMetafieldMappings || []).map((mapping, index) => ([
                              <TextField
                                key={`atd-attr-${index}`}
                                label={`ATD attribute ${index + 1}`}
                                labelHidden
                                autoComplete="off"
                                value={mapping.atdAttributeName || ""}
                                onChange={(value) => handleUpdateAttributeMapping(index, "atdAttributeName", value)}
                              />,
                              <TextField
                                key={`shopify-metafield-${index}`}
                                label={`Shopify metafield ${index + 1}`}
                                labelHidden
                                autoComplete="off"
                                value={mapping.shopifyProductMetafield || ""}
                                onChange={(value) => handleUpdateAttributeMapping(index, "shopifyProductMetafield", value)}
                                placeholder="namespace.key"
                              />,
                              <Select
                                key={`shopify-metafield-type-${index}`}
                                label={`Shopify metafield type ${index + 1}`}
                                labelHidden
                                options={atdMetafieldTypeOptions}
                                value={mapping.shopifyMetafieldType || "single_line_text_field"}
                                onChange={(value) => handleUpdateAttributeMapping(index, "shopifyMetafieldType", value)}
                              />,
                              <Button key={`remove-mapping-${index}`} tone="critical" onClick={() => handleRemoveAttributeMapping(index)}>
                                Remove
                              </Button>
                            ]))}
                          />
                        )}
                      </BlockStack>
                      {atdFilterProductSamples.length > 0 ? (
                        <DataTable
                          columnContentTypes={["text", "text", "text", "text"]}
                          headings={["Part number", "Product", "Brand", "Size values"]}
                          rows={atdFilterProductSamples.map((item) => [
                            item.partNumber || "-",
                            item.productName || "-",
                            item.brandName || "-",
                            item.sizeValues.length > 0 ? item.sizeValues.join(", ") : "-"
                          ])}
                        />
                      ) : null}
                      <Divider />
                      <BlockStack gap="200">
                        <Text as="h4" variant="headingSm">Create test product</Text>
                        <Text as="p" tone="subdued">
                          Push one ATD product into Shopify using the existing ATD Shopify object-manager flow, then review source vs Shopify fields below.
                        </Text>
                        {/* This action intentionally reuses the standard backend ATD push pipeline (single-product scoped)
                            so the comparison output reflects real sync behavior. */}
                        <InlineStack gap="200" align="start" blockAlign="end">
                          <div style={{ minWidth: "280px", flex: 1 }}>
                            <TextField
                              label="ATD part number"
                              autoComplete="off"
                              value={atdTestPartNumber}
                              onChange={setAtdTestPartNumber}
                              placeholder="e.g. 12345"
                              disabled={creatingAtdTestProduct}
                            />
                          </div>
                          <Button
                            variant="primary"
                            onClick={handleCreateAtdTestProduct}
                            loading={creatingAtdTestProduct}
                            disabled={creatingAtdTestProduct || !atdTestPartNumber.trim()}
                          >
                            Create test product
                          </Button>
                        </InlineStack>
                        {atdTestProductResult ? (
                          <Card>
                            <BlockStack gap="200">
                              <Banner title={atdTestProductResult.message} tone={atdTestProductResult.shopify ? "success" : "critical"}>
                                <p>Part number: {atdTestProductResult.partNumber}</p>
                                <p>ATD product ID: {atdTestProductResult.atdProductId}</p>
                                <p>Shopify product ID: {atdTestProductResult.shopifyProductId ?? "-"}</p>
                                {(atdTestProductResult.diagnostics || []).map((line, index) => (
                                  <p key={`atd-test-diagnostic-${index}`}>{line}</p>
                                ))}
                              </Banner>
                              <Text as="h5" variant="headingSm">ATD source data</Text>
                              <DataTable
                                columnContentTypes={["text", "text"]}
                                headings={["Field", "Value"]}
                                rows={[
                                  ["Product name", atdTestProductResult.source.productName || "-"],
                                  ["Brand", atdTestProductResult.source.brandName || "-"],
                                  ["Category", atdTestProductResult.source.categoryName || "-"],
                                  ["Discontinued", atdTestProductResult.source.isDiscontinued ? "Yes" : "No"],
                                  ["Available quantity", String(atdTestProductResult.source.availableQuantity ?? "-")],
                                  ["Weight", String(atdTestProductResult.source.weight ?? "-")],
                                  ["Primary image", atdTestProductResult.source.primaryImageUrl || "-"],
                                ]}
                              />
                              <Text as="h5" variant="headingSm">ATD attributes (sample)</Text>
                              <DataTable
                                columnContentTypes={["text", "text"]}
                                headings={["Attribute", "Value"]}
                                rows={(atdTestProductResult.source.attributes || []).length > 0
                                  ? atdTestProductResult.source.attributes.map((x) => [x.name || "-", x.value || "-"])
                                  : [["-", "No attributes"]]}
                              />
                              <Text as="h5" variant="headingSm">ATD prices (sample)</Text>
                              <DataTable
                                columnContentTypes={["text", "text"]}
                                headings={["Price type", "Amount"]}
                                rows={(atdTestProductResult.source.prices || []).length > 0
                                  ? atdTestProductResult.source.prices.map((x) => [x.priceType || "-", String(x.amount ?? "-")])
                                  : [["-", "No prices"]]}
                              />
                              <Text as="h5" variant="headingSm">Shopify product snapshot</Text>
                              <DataTable
                                columnContentTypes={["text", "text"]}
                                headings={["Field", "Value"]}
                                rows={atdTestProductResult.shopify ? [
                                  ["Product ID", String(atdTestProductResult.shopify.productId)],
                                  ["Title", atdTestProductResult.shopify.title || "-"],
                                  ["Vendor", atdTestProductResult.shopify.vendor || "-"],
                                  ["Product type", atdTestProductResult.shopify.productType || "-"],
                                  ["Handle", atdTestProductResult.shopify.handle || "-"],
                                  ["Status", atdTestProductResult.shopify.status || "-"],
                                  ["Variant SKU", atdTestProductResult.shopify.variantSku || "-"],
                                  ["Metafield length", String(atdTestProductResult.shopify.metafieldLength ?? "-")],
                                  ["Metafield width", String(atdTestProductResult.shopify.metafieldWidth ?? "-")],
                                  ["Metafield height", String(atdTestProductResult.shopify.metafieldHeight ?? "-")],
                                  ["Metafield weight", String(atdTestProductResult.shopify.metafieldWeight ?? "-")],
                                  ["Tags", atdTestProductResult.shopify.tags?.length ? atdTestProductResult.shopify.tags.join(", ") : "-"],
                                ] : [["-", "No Shopify product data returned"]]}
                              />
                            </BlockStack>
                          </Card>
                        ) : null}
                      </BlockStack>
                      <InlineStack align="end" gap="200">
                        <Button onClick={handleTestAtdFilterProducts} loading={testingAtdFilterProducts} disabled={testingAtdFilterProducts || savingAtdProfile}>
                          Test product filters
                        </Button>
                        <Button onClick={handleTestAtdProfile} loading={testingAtdProfile} disabled={testingAtdProfile || savingAtdProfile}>
                          Test connection
                        </Button>
                        <Button variant="primary" onClick={handleSaveAtdProfile} loading={savingAtdProfile} disabled={savingAtdProfile || testingAtdProfile}>
                          Save ATD profile
                        </Button>
                      </InlineStack>
                    </BlockStack>
                  </Card>
                </BlockStack>
              ) : null}

              <div hidden={selectedTab !== 4}>
                <BlockStack gap="400">
                  <StoreReviewPanel key={details.store.shopId} id={details.store.shopId} token={token} onUnauthorized={onUnauthorized} />
                  <PilotOperatorTools key={`audit-${details.store.shopId}`} sourceId={details.store.shopId} token={token} active={false} native domain={details.store.myShopifyDomain || details.store.domain} />
                </BlockStack>
              </div>

              {selectedTab === 5 ? (
                <BlockStack gap="200">
                  <Text as="h3" variant="headingMd">Fitment import history</Text>
                  {details.fitmentImportHistory.length === 0 ? (
                    <Text as="p">No fitment imports found for this store.</Text>
                  ) : (
                    <DataTable
                      columnContentTypes={["text", "text", "text", "text", "text", "text"]}
                      headings={["Queued", "File", "Format", "State", "Completed", "Action"]}
                      rows={details.fitmentImportHistory.map((item) => [
                        formatLocalDateTime(item.addedOn),
                        item.originalFileName || item.storedFileName || `Import ${item.queueId}`,
                        item.importFormat,
                        item.state,
                        formatLocalDateTime(item.completedAt),
                        <InlineStack key={`actions-${item.queueId}`} gap="200">
                          <Button
                            key={`download-${item.queueId}`}
                            size="slim"
                            onClick={() => handleDownloadFitmentImport(item)}
                            loading={downloadingImportQueueId === item.queueId}
                            disabled={downloadingImportQueueId !== null || deletingImportQueueId !== null || restartingImportQueueId !== null}
                          >
                            Download
                          </Button>
                          <Button
                            key={`restart-${item.queueId}`}
                            size="slim"
                            onClick={() => handleRestartFitmentImport(item)}
                            loading={restartingImportQueueId === item.queueId}
                            disabled={deletingImportQueueId !== null || downloadingImportQueueId !== null || restartingImportQueueId !== null}
                          >
                            Restart
                          </Button>
                          <Button
                            key={`delete-${item.queueId}`}
                            size="slim"
                            tone="critical"
                            onClick={() => handleDeleteFitmentImport(item)}
                            loading={deletingImportQueueId === item.queueId}
                            disabled={deletingImportQueueId !== null || downloadingImportQueueId !== null || restartingImportQueueId !== null}
                          >
                            Delete
                          </Button>
                        </InlineStack>
                      ])}
                    />
                  )}
                </BlockStack>
              ) : null}
            </div>
          </Card>
        </>
      )}
    </BlockStack>
  );
}
